<template>
    <div class="ticket-page">
        <ComponentHeader
            title="Support Tickets"
            :hasSearch="true"
            @search-query="(val) => (searchQuery = val)"
            :hasCreate="true"
            createButtonText="Create Ticket"
            @create-press="createTicket"
            :hasReload="false"
            :hasback="false"
        >
            <template #heading-before-search>
                <div class="ticket-filters">
                    <b-form-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        class="status-select"
                    ></b-form-select>
                </div>
            </template>
            <template #heading-actions>
                <div class="ml-2">
                    <div class="ticket-header-actions">
                        <div class="view-toggle-group">
                            <b-button
                                size="sm"
                                :variant="
                                    viewMode === 'board'
                                        ? 'primary'
                                        : 'outline-secondary'
                                "
                                class="toggle-btn"
                                @click="viewMode = 'board'"
                            >
                                <i class="fa fa-th-large"></i>
                            </b-button>
                            <b-button
                                size="sm"
                                :variant="
                                    viewMode === 'list'
                                        ? 'primary'
                                        : 'outline-secondary'
                                "
                                class="toggle-btn"
                                @click="viewMode = 'list'"
                            >
                                <i class="fa fa-list"></i>
                            </b-button>
                        </div>
                    </div>
                </div>
            </template>
        </ComponentHeader>

        <!-- Loading -->
        <div v-if="ticketListLoading" class="ticket-loading">
            <atom-spinner
                :animation-duration="1000"
                :size="60"
                color="#ff1d5e"
                class="my-5"
            />
            <p class="mt-2 mb-0">Loading tickets...</p>
        </div>

        <!-- Board Layout -->
        <b-card
            v-else-if="viewMode === 'board'"
            class="card-wrap ticket-board-card"
            border-variant="info"
        >
            <div class="ticket-board">
                <div
                    v-for="column in filteredColumns"
                    :key="column.key"
                    :class="['ticket-column', column.class]"
                >
                    <div class="column-header">
                        <div class="column-title-wrap">
                            <span class="column-title">{{ column.label }}</span>
                            <span class="column-count">
                                {{ ticketsByStatus[column.key].length }}
                            </span>
                        </div>
                    </div>
                    <div
                        class="column-body"
                        @dragover.prevent="handleDragOver($event, column.key)"
                        @drop.prevent="handleDrop($event, column.key)"
                        @dragenter.prevent="handleDragEnter($event, column.key)"
                        @dragleave.prevent="handleDragLeave($event)"
                        :class="{ 'drag-over': draggedOverColumn === column.key }"
                    >
                        <div
                            v-for="ticket in ticketsByStatus[column.key]"
                            :key="ticket.id"
                            class="ticket-card"
                            :class="{ dragging: draggedTicketId === ticket.id }"
                            draggable="true"
                            @dragstart="handleDragStart($event, ticket)"
                            @dragend="handleDragEnd"
                            @click="viewTicket(ticket)"
                        >
                            <div class="ticket-card-top">
                                <span class="ticket-id">{{ ticket.id }}</span>
                                <span
                                    v-if="ticket.priority"
                                    :class="[
                                        'ticket-priority',
                                        'priority-' +
                                            ticket.priority.toLowerCase(),
                                    ]"
                                >
                                    {{ ticket.priority }}
                                </span>
                            </div>
                            <div class="ticket-subject">
                                {{ ticket.title }}
                            </div>
                            <div class="ticket-card-footer">
                                <div class="ticket-assignee">
                                    <span class="assignee-avatar">
                                        {{ ticket.initials }}
                                    </span>
                                    <div class="assignee-info">
                                        <span class="assignee-name">
                                            {{ ticket.assignee }}
                                        </span>
                                    </div>
                                </div>
                                <div class="ticket-date">
                                    <i class="fa fa-calendar mr-1"></i>
                                    <span>{{ ticket.date }}</span>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="!ticketsByStatus[column.key].length"
                            class="column-empty"
                        >
                            <span>No tickets</span>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- List View -->
        <b-card
            v-else-if="viewMode === 'list'"
            class="card-wrap ticket-table-card"
            border-variant="info"
        >
            <div class="table-wrapper">
                <b-table
                    id="ticket-list-table"
                    :items="filteredTickets"
                    :fields="listTableFields"
                    :per-page="listPerPage"
                    :current-page="listCurrentPage"
                    :hover="true"
                    :striped="false"
                    :bordered="true"
                    :small="false"
                    responsive="sm"
                    show-empty
                    empty-text="No tickets found"
                    thead-class="header"
                    class="ticket-list-table"
                    tbody-tr-class="ticket-table-row"
                    @row-clicked="viewTicket"
                >
                    <template #cell(id)="{ item }">
                        <span class="ticket-id-link">{{ item.id }}</span>
                    </template>

                    <template #cell(title)="{ item }">
                        <span class="ticket-subject-text">
                            {{ item.title }}
                        </span>
                    </template>

                    <template #cell(status)="{ item }">
                        <span
                            :class="[
                                'status-badge',
                                'status-' +
                                    item.status
                                        .toLowerCase()
                                        .replace(/_/g, '-'),
                            ]"
                        >
                            {{ item.status.replace(/_/g, " ") }}
                        </span>
                    </template>

                    <template #cell(priority)="{ item }">
                        <span
                            v-if="item.priority"
                            :class="[
                                'priority-badge',
                                'priority-' + item.priority.toLowerCase(),
                            ]"
                        >
                            {{ item.priority }}
                        </span>
                    </template>

                    <template #cell(assignee)="{ item }">
                        <div class="assignee-wrapper">
                            <span class="assignee-avatar-small">
                                {{ item.initials }}
                            </span>
                            <span class="assignee-name-small">
                                {{ item.assignee }}
                            </span>
                        </div>
                    </template>

                    <template #cell(date)="{ item }">
                        <span class="ticket-date-text">
                            <i class="fa fa-calendar mr-1"></i>
                            {{ item.date }}
                        </span>
                    </template>
                </b-table>
            </div>

            <div
                v-if="filteredTickets && filteredTickets.length"
                class="pagination-wrapper"
            >
                <b-pagination
                    v-model="listCurrentPage"
                    :total-rows="filteredTickets.length"
                    :per-page="listPerPage"
                    size="md"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                    aria-controls="ticket-list-table"
                ></b-pagination>
            </div>
        </b-card>

        <!-- Empty state when no tickets and not loading -->
        <div
            v-else-if="!ticketListLoading && !tickets.length"
            class="ticket-empty-state"
        >
            <p class="text-muted mb-0">
                No tickets yet. Create one to get started.
            </p>
        </div>

        <!-- Create Ticket Modal -->
        <b-modal
            id="create-ticket-modal"
            v-model="showCreateModal"
            title="Create Ticket"
            size="lg"
            centered
            hide-footer
            class="create-ticket-modal"
            @hidden="resetForm"
        >
            <template #modal-header>
                <h5 class="modal-title">Create Ticket</h5>
                <button
                    type="button"
                    class="close"
                    @click="showCreateModal = false"
                    aria-label="Close"
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </template>

            <b-form @submit.prevent="handleCreateTicket">
                <div class="form-group">
                    <label for="category">
                        CATEGORY <span class="required">*</span>
                    </label>
                    <b-form-select
                        id="category"
                        v-model="ticketForm.category"
                        :options="categoryOptions"
                        :state="validation.category"
                        placeholder="Select a category"
                        class="form-control"
                    >
                        <template #first>
                            <b-form-select-option :value="null" disabled>
                                Select a category
                            </b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback
                        v-if="validation.category === false"
                    >
                        Category is required
                    </b-form-invalid-feedback>
                </div>

                <!-- Subject (Required) -->
                <div class="form-group">
                    <label for="subject">
                        SUBJECT <span class="required">*</span>
                    </label>
                    <b-form-input
                        id="subject"
                        v-model="ticketForm.subject"
                        :state="validation.subject"
                        placeholder="Enter ticket subject"
                        class="form-control"
                    ></b-form-input>
                    <b-form-invalid-feedback
                        v-if="validation.subject === false"
                    >
                        Subject is required
                    </b-form-invalid-feedback>
                </div>

                <!-- Description (Required) -->
                <div class="form-group">
                    <label for="description">
                        DESCRIPTION <span class="required">*</span>
                    </label>
                    <b-form-textarea
                        id="description"
                        v-model="ticketForm.description"
                        :state="validation.description"
                        placeholder="Enter ticket description"
                        rows="4"
                        class="form-control"
                    ></b-form-textarea>
                    <b-form-invalid-feedback
                        v-if="validation.description === false"
                    >
                        Description is required
                    </b-form-invalid-feedback>
                </div>

                <!-- Scope and Severity Row -->
                <div class="form-row">
                    <div class="form-group col-md-6">
                        <label for="scope">SCOPE</label>
                        <b-form-input
                            id="scope"
                            v-model="ticketForm.scope"
                            placeholder="Scope (auto-filled)"
                            class="form-control"
                            disabled
                        ></b-form-input>
                    </div>
                    <div class="form-group col-md-6">
                        <label for="severity">SEVERITY</label>
                        <b-form-select
                            id="severity"
                            v-model="ticketForm.severity"
                            :options="severityOptions"
                            class="form-control"
                        ></b-form-select>
                    </div>
                </div>

                <!-- Priority -->
                <div class="form-group">
                    <label for="priority">PRIORITY (0-10)</label>
                    <b-form-input
                        id="priority"
                        v-model.number="ticketForm.priority"
                        type="number"
                        min="0"
                        max="10"
                        class="form-control"
                    ></b-form-input>
                </div>

                <!-- Assign To and Reported To Row -->
                <div class="form-row">
                    <div class="form-group col-md-6">
                        <label for="assignTo">ASSIGN TO</label>
                        <b-form-select
                            id="assignTo"
                            v-model="ticketForm.assignTo"
                            :options="userOptions"
                            placeholder="Select a user"
                            class="form-control"
                        >
                            <template #first>
                                <b-form-select-option :value="null">
                                    Select a user
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>
                    <div class="form-group col-md-6">
                        <label for="reportedTo">REPORTED TO</label>
                        <b-form-select
                            id="reportedTo"
                            v-model="ticketForm.reportedTo"
                            :options="userOptions"
                            placeholder="Select a user"
                            class="form-control"
                        >
                            <template #first>
                                <b-form-select-option :value="null">
                                    Select a user
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group col-md-6">
                        <label for="startDateTime">START DATE & TIME</label>
                        <b-input-group>
                            <b-form-input
                                id="startDateTime"
                                v-model="ticketForm.startDateTime"
                                type="datetime-local"
                                class="form-control"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-input-group-text>
                                    <i class="fa fa-calendar"></i>
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="form-group col-md-6">
                        <label for="endDateTime">END DATE & TIME</label>
                        <b-input-group>
                            <b-form-input
                                id="endDateTime"
                                v-model="ticketForm.endDateTime"
                                type="datetime-local"
                                class="form-control"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-input-group-text>
                                    <i class="fa fa-calendar"></i>
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                </div>

                <!-- Form Actions -->
                <div
                    class="modal-actions d-flex justify-content-center align-items-center"
                    style="gap: 20px; width: 100%"
                >
                    <b-button
                        type="button"
                        variant="secondary"
                        @click="showCreateModal = false"
                        class="btn-cancel"
                        style="
                            min-width: 60px;
                            width: auto;
                            max-width: 120px;
                            padding: 6px 12px;
                            font-size: 0.8125rem;
                            flex: 0 0 auto;
                        "
                    >
                        Cancel
                    </b-button>
                    <b-button
                        type="submit"
                        variant="primary"
                        class="btn-create"
                        :disabled="isSubmitting"
                        style="
                            min-width: 85px;
                            width: auto;
                            max-width: 140px;
                            padding: 6px 12px;
                            font-size: 0.8125rem;
                            flex: 0 0 auto;
                        "
                    >
                        <span v-if="isSubmitting">
                            <i class="fa fa-spinner fa-spin mr-1"></i>
                            Creating...
                        </span>
                        <span v-else>Create Ticket</span>
                    </b-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import { mapGetters } from "vuex";
import { AtomSpinner } from "epic-spinners";

export default {
    name: "ChannelTicketManagement",
    components: {
        ComponentHeader,
        AtomSpinner,
    },
    data() {
        return {
            viewMode: "board",
            searchQuery: "",
            statusFilter: "ALL",
            listTableFields: [
                {
                    key: "id",
                    label: "Ticket ID",
                    thClass: "col-id",
                    tdClass: "col-id",
                },
                {
                    key: "title",
                    label: "Subject",
                    thClass: "col-subject",
                    tdClass: "col-subject",
                },
                {
                    key: "status",
                    label: "Status",
                    thClass: "col-status",
                    tdClass: "col-status",
                },
                {
                    key: "priority",
                    label: "Priority",
                    thClass: "col-priority",
                    tdClass: "col-priority",
                },
                {
                    key: "assignee",
                    label: "Assignee",
                    thClass: "col-assignee",
                    tdClass: "col-assignee",
                },
                {
                    key: "date",
                    label: "Date",
                    thClass: "col-date",
                    tdClass: "col-date",
                },
            ],
            listCurrentPage: 1,
            listPerPage: 10,
            draggedTicketId: null,
            draggedOverColumn: null,
            draggedTicket: null,
            ticketListLoading: false,
            showCreateModal: false,
            isSubmitting: false,
            ticketForm: {
                category: null,
                subject: "",
                description: "",
                scope: "",
                severity: "Medium",
                priority: 5,
                assignTo: null,
                reportedTo: null,
                startDateTime: "",
                endDateTime: "",
            },
            validation: {
                category: null,
                subject: null,
                description: null,
            },
            severityOptions: [
                { value: "Low", text: "Low" },
                { value: "Medium", text: "Medium" },
                { value: "High", text: "High" },
                { value: "Critical", text: "Critical" },
            ],
            statusOptions: [
                { value: "ALL", text: "All Status" },
                { value: "NEW", text: "New" },
                { value: "IN_PROGRESS", text: "In Progress" },
                { value: "RESOLVED", text: "Resolved" },
                { value: "CLOSED", text: "Closed" },
            ],
            columns: [
                {
                    key: "NEW",
                    label: "NEW",
                    class: "column-new",
                },
                {
                    key: "IN_PROGRESS",
                    label: "IN PROGRESS",
                    class: "column-in-progress",
                },
                {
                    key: "RESOLVED",
                    label: "RESOLVED",
                    class: "column-resolved",
                },
                {
                    key: "CLOSED",
                    label: "CLOSED",
                    class: "column-closed",
                },
            ],
        };
    },
    computed: {
        ...mapGetters("ticketManagement", [
            "GetTicketList",
            "GetCategoryList",
            "GetAdminList",
        ]),
        categoryOptions() {
            const list = this.GetCategoryList || [];
            return (list || []).filter(Boolean).map((c) => ({
                value: c._id,
                text: (c && (c.name || c.description)) || "—",
            }));
        },
        userOptions() {
            const list = this.GetAdminList || [];
            return (list || []).filter(Boolean).map((a) => {
                const nameObj = (a && a.name) || {};
                const first = nameObj.first || "";
                const last = nameObj.last || "";
                const text =
                    [first, last].filter(Boolean).join(" ") ||
                    (a.authentication && a.authentication.userName) ||
                    "—";
                return { value: a._id, text };
            });
        },
        tickets() {
            const raw = this.GetTicketList || [];
            return raw
                .map((item) => this.mapApiTicketToUi(item))
                .filter(Boolean);
        },
        filteredTickets() {
            const q = (this.searchQuery || "").toLowerCase().trim();
            if (!q) {
                return this.tickets;
            }
            return this.tickets.filter((t) => {
                return (
                    (t.id && String(t.id).toLowerCase().includes(q)) ||
                    (t.title && t.title.toLowerCase().includes(q)) ||
                    (t.ticketNo && t.ticketNo.toLowerCase().includes(q))
                );
            });
        },
        ticketsByStatus() {
            const map = {
                NEW: [],
                IN_PROGRESS: [],
                RESOLVED: [],
                CLOSED: [],
            };
            this.filteredTickets.forEach((t) => {
                const key = t.status || "NEW";
                if (map[key]) {
                    map[key].push(t);
                }
            });
            return map;
        },
        filteredColumns() {
            if (this.statusFilter === "ALL") {
                return this.columns;
            }
            return this.columns.filter(
                (column) => column.key === this.statusFilter,
            );
        },
    },
    watch: {
        filteredTickets() {
            this.listCurrentPage = 1;
        },
        listPerPage() {
            this.listCurrentPage = 1;
        },
        "ticketForm.category"(categoryId) {
            if (!categoryId) {
                this.ticketForm.scope = "";
                return;
            }
            const list = this.GetCategoryList || [];
            const cat = list.find((c) => c._id === categoryId);
            if (cat) {
                this.ticketForm.scope = cat.scope || "";
                if (cat.severity) {
                    this.ticketForm.severity = cat.severity;
                }
            }
        },
    },
    mounted() {
        this.loadTicketList();
        this.$store.dispatch("ticketManagement/fetchAdminList", {
            limit: 50,
            page: 1,
        });
        this.$store.dispatch("ticketManagement/fetchCategoryList");
    },
    methods: {
        mapApiStatusToBoard(apiStatus) {
            if (!apiStatus) return "NEW";
            const s = String(apiStatus).toUpperCase();
            if (s === "OPEN") return "NEW";
            if (
                [
                    "NEW",
                    "IN_PROGRESS",
                    "IN PROGRESS",
                    "RESOLVED",
                    "CLOSED",
                ].indexOf(s) !== -1
            ) {
                return s === "IN PROGRESS" ? "IN_PROGRESS" : s;
            }
            return "NEW";
        },
        priorityNumberToLabel(num) {
            if (num == null) return "MEDIUM";
            const n = Number(num);
            if (n >= 8) return "CRITICAL";
            if (n >= 6) return "HIGH";
            if (n >= 4) return "MEDIUM";
            return "LOW";
        },
        getInitials(name) {
            if (!name || typeof name !== "string") return "NA";
            return name
                .trim()
                .split(/\s+/)
                .map((part) => part[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        },
        formatTicketDate(dateStr) {
            if (!dateStr) return "";
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return "";
            return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
        },
        mapApiTicketToUi(item) {
            if (!item) return null;
            const assignTo = item.assignTo || {};
            const name =
                (assignTo &&
                    typeof assignTo.name === "string" &&
                    assignTo.name) ||
                "Unassigned";
            const status = this.mapApiStatusToBoard(item.status);
            const priorityNum = item.priority != null ? item.priority : 5;
            const ticketNo = item.ticketNo
                ? item.ticketNo.startsWith("#")
                    ? item.ticketNo
                    : "#" + item.ticketNo
                : item._id || "";
            return {
                _id: item._id,
                id: ticketNo,
                ticketNo: item.ticketNo,
                title: item.subject || "",
                status,
                priority: this.priorityNumberToLabel(priorityNum),
                priorityValue: priorityNum,
                assignee: name,
                initials: this.getInitials(name),
                date: this.formatTicketDate(item.createdAt || item.updatedAt),
                raw: item,
            };
        },
        async loadTicketList() {
            this.ticketListLoading = true;
            try {
                await this.$store.dispatch("ticketManagement/fetchTicketList");
            } catch (err) {
                this.$bvToast.toast(err.message || "Failed to load tickets", {
                    title: "Error",
                    variant: "danger",
                    solid: true,
                });
            } finally {
                this.ticketListLoading = false;
            }
        },
        createTicket() {
            this.showCreateModal = true;
        },
        resetForm() {
            this.ticketForm = {
                category: null,
                subject: "",
                description: "",
                scope: "",
                severity: "Medium",
                priority: 5,
                assignTo: null,
                reportedTo: null,
                startDateTime: "",
                endDateTime: "",
            };
            this.validation = {
                category: null,
                subject: null,
                description: null,
            };
            this.isSubmitting = false;
        },
        validateForm() {
            let isValid = true;

            // Validate category
            if (!this.ticketForm.category) {
                this.validation.category = false;
                isValid = false;
            } else {
                this.validation.category = true;
            }

            // Validate subject
            if (
                !this.ticketForm.subject ||
                this.ticketForm.subject.trim() === ""
            ) {
                this.validation.subject = false;
                isValid = false;
            } else {
                this.validation.subject = true;
            }

            // Validate description
            if (
                !this.ticketForm.description ||
                this.ticketForm.description.trim() === ""
            ) {
                this.validation.description = false;
                isValid = false;
            } else {
                this.validation.description = true;
            }

            return isValid;
        },
        buildAdminPayload(admin) {
            if (!admin) return null;
            const nameObj = admin.name || {};
            const first = nameObj.first || "";
            const last = nameObj.last || "";
            const name =
                [first, last].filter(Boolean).join(" ") ||
                (admin.authentication && admin.authentication.userName) ||
                "";
            return {
                id: admin._id,
                name,
                email:
                    (admin.authentication && admin.authentication.email) || "",
                userType: admin.category || "EMPLOYEE",
                phone:
                    (admin.authentication && admin.authentication.phone) || "",
            };
        },
        async handleCreateTicket() {
            if (!this.validateForm()) {
                return;
            }

            this.isSubmitting = true;

            const adminList = this.GetAdminList || [];
            const assignAdmin = adminList.find(
                (a) => a._id === this.ticketForm.assignTo,
            );
            const reportAdmin = adminList.find(
                (a) => a._id === this.ticketForm.reportedTo,
            );

            const assignToPayload = this.buildAdminPayload(assignAdmin);
            const reportedBy = assignToPayload;

            const startDate = this.ticketForm.startDateTime
                ? new Date(this.ticketForm.startDateTime).toISOString()
                : null;
            const endDate = this.ticketForm.endDateTime
                ? new Date(this.ticketForm.endDateTime).toISOString()
                : null;

            const payload = {
                category: this.ticketForm.category,
                description: (this.ticketForm.description || "").trim(),
                subject: (this.ticketForm.subject || "").trim(),
                reportedBy,
                attachments: [],
                priority: Number(this.ticketForm.priority) || 5,
                severity: this.ticketForm.severity || "Medium",
                reportedTo: this.buildAdminPayload(reportAdmin),
                assignTo: assignToPayload,
                startDate,
                endDate,
            };

            try {
                await this.$store.dispatch(
                    "ticketManagement/createTicketAction",
                    payload,
                );
                this.isSubmitting = false;
                this.showCreateModal = false;
                this.resetForm();
                this.$bvToast.toast("Ticket created successfully", {
                    title: "Success",
                    variant: "success",
                    solid: true,
                    autoHideDelay: 3000,
                });
            } catch (err) {
                this.isSubmitting = false;
                this.$bvToast.toast(
                    (err && err.getMessage && err.getMessage()) ||
                        (err && err.message) ||
                        "Failed to create ticket",
                    {
                        title: "Error",
                        variant: "danger",
                        solid: true,
                    },
                );
            }
        },
        viewTicket(ticket) {
            if (this.draggedTicketId) {
                return;
            }
            const id = ticket._id || ticket.raw?._id;
            if (!id) return;
            this.$router.push({
                name: "ticketManagement-view",
                params: { id, ticket },
            });
        },
        handleDragStart(event, ticket) {
            this.draggedTicketId = ticket.id;
            this.draggedTicket = ticket;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", ticket.id);
            event.target.style.opacity = "0.5";
        },
        handleDragOver(event, columnKey) {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            this.draggedOverColumn = columnKey;
            if (this._dragLeaveTimeout) {
                clearTimeout(this._dragLeaveTimeout);
                this._dragLeaveTimeout = null;
            }
        },
        handleDragEnter(event, columnKey) {
            event.preventDefault();
            this.draggedOverColumn = columnKey;
        },
        handleDragLeave(event) {
            const relatedTarget = event.relatedTarget;
            const currentTarget = event.currentTarget;
            if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
                this._dragLeaveTimeout = setTimeout(() => {
                    const elementBelow = document.elementFromPoint(
                        event.clientX,
                        event.clientY,
                    );
                    if (!currentTarget.contains(elementBelow)) {
                        this.draggedOverColumn = null;
                    }
                }, 100);
            }
        },
        handleDrop(event, targetColumnKey) {
            event.preventDefault();

            if (!this.draggedTicket) {
                return;
            }

            const sourceColumnKey = this.draggedTicket.status;
            if (sourceColumnKey !== targetColumnKey) {
                const ticket = this.draggedTicket;
                const ticketId = ticket._id || ticket.id;
                if (!ticketId) return;
                this.onTicketStatusChange(
                    ticket,
                    sourceColumnKey,
                    targetColumnKey,
                );
            }
            this.draggedOverColumn = null;
        },
        handleDragEnd(event) {
            this.draggedTicketId = null;
            this.draggedTicket = null;
            this.draggedOverColumn = null;
            if (this._dragLeaveTimeout) {
                clearTimeout(this._dragLeaveTimeout);
                this._dragLeaveTimeout = null;
            }
            event.target.style.opacity = "1";
        },
        boardStatusToApiStatus(boardStatus) {
            if (boardStatus === "NEW") return "OPEN";
            return boardStatus;
        },
        ensureNameObj(obj) {
            if (obj == null) return { name: "—" };
            if (typeof obj !== "object") return { name: "—" };
            const name =
                (obj.name && String(obj.name).trim()) ||
                (obj.first != null && obj.last != null
                    ? [obj.first, obj.last].filter(Boolean).join(" ").trim()
                    : "") ||
                (obj.email && String(obj.email)) ||
                "—";
            return { ...obj, name: name || "—" };
        },
        getCurrentUserAsUpdatedBy() {
            const auth = this.$store.getters["auth/getloginUser"] || {};
            const nameObj = auth.name || {};
            const name =
                (auth.name &&
                    typeof auth.name === "string" &&
                    auth.name.trim()) ||
                (nameObj.first != null && nameObj.last != null
                    ? [nameObj.first, nameObj.last]
                          .filter(Boolean)
                          .join(" ")
                          .trim()
                    : "") ||
                (auth.userName && String(auth.userName).trim()) ||
                (auth.email && String(auth.email)) ||
                "—";
            return {
                id: auth._id || auth.id,
                name: name || "—",
                email: auth.email || "",
                phone: auth.phone || "",
                userType: auth.userType || auth.userRole || "EMPLOYEE",
            };
        },
        async onTicketStatusChange(ticket, oldStatus, newStatus) {
            const id = ticket._id;
            if (!id) return;
            const apiStatus = this.boardStatusToApiStatus(newStatus);
            const raw = (ticket && ticket.raw) || {};
            const worknoteHistory = (raw.worknoteHistory || []).map((item) =>
                item && item.updatedBy
                    ? {
                          ...item,
                          updatedBy: this.ensureNameObj(item.updatedBy),
                      }
                    : item,
            );
            const changeHistory = (raw.changeHistory || []).map((item) =>
                item && item.updatedBy
                    ? {
                          ...item,
                          updatedBy: this.ensureNameObj(item.updatedBy),
                      }
                    : item,
            );
            const currentUser = this.getCurrentUserAsUpdatedBy();
            const updatePayload = {
                ...raw,
                _id: id,
                status: apiStatus,
                reportedBy: this.ensureNameObj(raw.reportedBy),
                assignTo: this.ensureNameObj(raw.assignTo),
                reportedTo: this.ensureNameObj(raw.reportedTo),
                updatedBy: currentUser,
                worknoteHistory,
                changeHistory,
            };
            try {
                await this.$store.dispatch(
                    "ticketManagement/updateTicketAction",
                    {
                        id,
                        data: updatePayload,
                    },
                );
                await this.loadTicketList();
                this.$bvToast.toast("Ticket status updated", {
                    title: "Success",
                    variant: "success",
                    solid: true,
                    autoHideDelay: 2000,
                });
            } catch (err) {
                this.$bvToast.toast(
                    err.message || "Failed to update ticket status",
                    {
                        title: "Error",
                        variant: "danger",
                        solid: true,
                    },
                );
            }
        },
    },
};
</script>

<style scoped>
.ticket-page {
    min-height: 100vh;
    width: 100%;
    background-color: #f7f8f9;
    overflow-x: hidden;
}

.ticket-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    color: #5e6c84;
}

.ticket-empty-state {
    padding: 48px 24px;
    text-align: center;
    color: #8993a4;
}

/* Header (ComponentHeader slots) */
.ticket-header-actions {
    display: inline-flex;
    align-items: center;
    gap: 12px;
}

::v-deep .component-header-wrapper {
    margin-bottom: 10px;
}

::v-deep .component-header-navbar {
    padding-bottom: 6px;
}

.view-toggle-group {
    display: inline-flex;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #dfe1e6;
    background: #fff;
    box-shadow: 0 1px 2px rgba(9, 30, 66, 0.08);
}

.toggle-btn {
    border-radius: 0;
    padding: 8px 12px;
    transition: all 0.2s ease;
    box-shadow: none;
}

.toggle-btn:first-child {
    border-radius: 6px 0 0 6px;
}

.toggle-btn:last-child {
    border-radius: 0 6px 6px 0;
}

.toggle-btn:focus,
.toggle-btn:active {
    box-shadow: none !important;
}

.view-toggle-group .toggle-btn:not(.btn-primary):hover {
    background-color: #f4f5f7;
    color: #0052cc;
}

.btn-new-ticket {
    border-radius: 6px;
    font-weight: 600;
    padding: 10px 20px;
    background: #0052cc;
    border: none;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 82, 204, 0.2);
}

.btn-new-ticket:hover {
    background: #0065ff;
    box-shadow: 0 4px 8px rgba(0, 82, 204, 0.3);
    transform: translateY(-1px);
}

.btn-new-ticket:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 82, 204, 0.2);
}

/* Filters */
.ticket-filters {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
}

.filter-search {
    flex: 1 1 auto;
    min-width: 280px;
    display: flex !important;
    flex-wrap: nowrap !important;
    align-items: stretch;
}

.filter-search >>> .input-group-prepend {
    width: auto !important;
    flex-shrink: 0 !important;
    margin-top: 0 !important;
}

.filter-search >>> .input-group-text {
    background: #fff;
    border: 2px solid #dfe1e6;
    border-right: 0;
    border-radius: 6px 0 0 6px;
    color: #6b778c;
    padding: 10px 12px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.filter-search >>> .form-control {
    border: 2px solid #dfe1e6;
    border-left: 0;
    border-radius: 0 6px 6px 0;
    padding: 10px 12px;
    font-size: 14px;
    transition: all 0.2s ease;
    background-color: #fafbfc;
    flex: 1;
    min-width: 0;
}

.filter-search >>> .form-control:focus {
    outline: none;
    border-color: #0052cc;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.1);
}

.filter-search >>> .form-control:focus + .input-group-text,
.filter-search >>> .form-control:focus ~ .input-group-text {
    border-color: #0052cc;
}

.status-select {
    min-width: 160px;
    border-radius: 6px;
    border: 2px solid #dfe1e6;
    font-size: 14px;
    background-color: #fafbfc;
    transition: all 0.2s ease;
}

.status-select:focus {
    outline: none;
    border-color: #0052cc;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.1);
}

/* Board */
.ticket-board {
    display: grid;
    grid-template-columns: repeat(4, minmax(280px, 1fr));
    gap: 20px;
    align-items: stretch;
    max-width: 100%;
    padding-bottom: 2px; /* keep shadows from clipping */
}

.ticket-board-card >>> .card-body {
    padding: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    margin-right: 18px;
}

/* Columns */
.ticket-column {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    padding: 14px 14px 12px;
    min-height: 400px;
    background: #fff;
    border: 1px solid #dfe1e6;
    box-shadow: 0 1px 2px rgba(9, 30, 66, 0.06);
}

.column-new {
    background: linear-gradient(
        to bottom,
        rgba(255, 171, 0, 0.08) 0%,
        #fff 60%
    );
    border-top: 3px solid #ffab00;
}

.column-in-progress {
    background: linear-gradient(
        to bottom,
        rgba(0, 82, 204, 0.08) 0%,
        #fff 60%
    );
    border-top: 3px solid #0052cc;
}

.column-resolved {
    background: linear-gradient(
        to bottom,
        rgba(54, 179, 126, 0.08) 0%,
        #fff 60%
    );
    border-top: 3px solid #36b37e;
}

.column-closed {
    background: linear-gradient(
        to bottom,
        rgba(107, 119, 140, 0.08) 0%,
        #fff 60%
    );
    border-top: 3px solid #6b778c;
}

.column-header {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #dfe1e6;
}

.column-title-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.column-title {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: #5e6c84;
    text-transform: uppercase;
}

.column-count {
    min-width: 26px;
    height: 26px;
    border-radius: 999px;
    background: rgba(223, 225, 230, 0.9);
    color: #172b4d;
    font-size: 0.75rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
}

.column-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 300px);
    padding-right: 4px;
    transition: all 0.2s ease;
    min-height: 100px;
    padding-top: 2px;
}

.column-body.drag-over {
    background-color: rgba(0, 82, 204, 0.06);
    outline: 2px dashed rgba(0, 82, 204, 0.7);
    outline-offset: -2px;
    border-radius: 8px;
}

.column-body::-webkit-scrollbar {
    width: 6px;
}

.column-body::-webkit-scrollbar-track {
    background: transparent;
}

.column-body::-webkit-scrollbar-thumb {
    background: #dfe1e6;
    border-radius: 3px;
}

.column-body::-webkit-scrollbar-thumb:hover {
    background: #b3bac5;
}

/* Ticket cards */
.ticket-card {
    background: #fff;
    border-radius: 8px;
    padding: 12px 12px 10px;
    box-shadow: 0 1px 2px rgba(9, 30, 66, 0.1);
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #dfe1e6;
    user-select: none;
    position: relative;
}

.ticket-card:active {
    cursor: grabbing;
}

.ticket-card:hover:not(.dragging) {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(9, 30, 66, 0.12);
    background-color: #fafbfc;
    border-color: #b3bac5;
}

.ticket-card.dragging {
    opacity: 0.5;
    transform: rotate(3deg);
    cursor: grabbing;
    box-shadow: 0 10px 22px rgba(9, 30, 66, 0.18);
}

.ticket-card.dragging:hover {
    transform: rotate(3deg);
}

.ticket-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
    gap: 8px;
}

.ticket-id {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0052cc;
    font-family:
        -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
        Cantarell, sans-serif;
}

.ticket-id:hover {
    text-decoration: underline;
}

.ticket-priority {
    font-size: 0.6875rem;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    flex-shrink: 0;
}

.priority-high {
    background: #fff3e6;
    color: #c2410c;
}

.priority-critical {
    background: #fee2e2;
    color: #b91c1c;
}

.priority-medium {
    background: #e3f2fd;
    color: #1565c0;
}

.priority-low {
    background: #f1f3f5;
    color: #495057;
}

.ticket-subject {
    font-size: 0.9rem;
    font-weight: 700;
    color: #172b4d;
    margin-bottom: 12px;
    line-height: 1.4;
    word-break: break-word;
}

.ticket-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f1f3f5;
}

.ticket-assignee {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
}

.assignee-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0052cc, #0747a6);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 82, 204, 0.2);
}

.assignee-name {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #42526e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ticket-date {
    font-size: 0.75rem;
    color: #6b778c;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.ticket-date i {
    font-size: 0.6875rem;
}

/* Empty state */
.column-empty {
    margin-top: 16px;
    padding: 24px 16px;
    font-size: 0.875rem;
    color: #8993a4;
    text-align: center;
    border: 2px dashed #dfe1e6;
    border-radius: 8px;
    background: #fafbfc;
}

/* List View */
.ticket-table-card {
    overflow: hidden;
}

.table-wrapper {
    overflow-x: auto;
    width: 100%;
}

.pagination-wrapper {
    float: right;
    margin-top: 1rem;
}

::v-deep #ticket-list-table {
    table-layout: fixed;
}

::v-deep #ticket-list-table thead th {
    white-space: nowrap;
}

::v-deep #ticket-list-table tbody tr.ticket-table-row {
    cursor: pointer;
}

::v-deep #ticket-list-table th.col-id,
::v-deep #ticket-list-table td.col-id {
    width: 140px;
}

::v-deep #ticket-list-table th.col-status,
::v-deep #ticket-list-table td.col-status,
::v-deep #ticket-list-table th.col-priority,
::v-deep #ticket-list-table td.col-priority,
::v-deep #ticket-list-table th.col-date,
::v-deep #ticket-list-table td.col-date {
    width: 120px;
    white-space: nowrap;
}

::v-deep #ticket-list-table th.col-assignee,
::v-deep #ticket-list-table td.col-assignee {
    width: 180px;
}

.ticket-id-link {
    font-weight: 600;
    color: #0052cc;
    font-family:
        -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
        Cantarell, sans-serif;
}

.ticket-id-link:hover {
    text-decoration: underline;
}

.ticket-subject-text {
    font-weight: 600;
    color: #172b4d;
    line-height: 1.4;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
}

.status-badge.status-new {
    background: #fff3e6;
    color: #c2410c;
}

.status-badge.status-in-progress {
    background: #e3f2fd;
    color: #1565c0;
}

.status-badge.status-resolved {
    background: #e8f5e9;
    color: #2e7d32;
}

.status-badge.status-closed {
    background: #f1f3f5;
    color: #495057;
}

.priority-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.priority-badge.priority-high {
    background: #fff3e6;
    color: #c2410c;
}

.priority-badge.priority-critical {
    background: #fee2e2;
    color: #b91c1c;
}

.priority-badge.priority-medium {
    background: #e3f2fd;
    color: #1565c0;
}

.priority-badge.priority-low {
    background: #f1f3f5;
    color: #495057;
}

.assignee-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.assignee-avatar-small {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0052cc, #0747a6);
    color: #fff;
    font-size: 0.6875rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.assignee-name-small {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #42526e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ticket-date-text {
    font-size: 0.8125rem;
    color: #6b778c;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.ticket-date-text i {
    font-size: 0.75rem;
}

.list-empty {
    padding: 48px 24px;
    text-align: center;
    color: #8993a4;
    font-size: 0.9375rem;
}

/* Responsive */
@media (max-width: 1400px) {
    .ticket-board {
        grid-template-columns: repeat(4, minmax(260px, 1fr));
        gap: 16px;
    }
}

@media (max-width: 1200px) {
    .ticket-board {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .ticket-page {
        padding: 16px;
    }

    .ticket-board {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .ticket-filters {
        flex-direction: column;
        align-items: stretch;
    }

    .filter-search {
        min-width: 100%;
    }

    /* Keep search icon and input on same row (override _custom.scss) */
    .filter-search {
        flex-wrap: nowrap !important;
        width: 100%;
    }

    .filter-search >>> .input-group-prepend {
        width: auto !important;
        margin-top: 0 !important;
    }

    .filter-search >>> .input-group-text,
    .filter-search >>> .form-control {
        height: 44px;
        display: flex;
        align-items: center;
    }

    .status-select {
        width: 100%;
    }

    .pagination-wrapper {
        float: none;
        width: 100%;
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }

    .column-body {
        max-height: none;
    }
}

@media (max-width: 480px) {
    .ticket-page {
        padding: 12px;
    }

    .view-toggle-group {
        width: 100%;
        justify-content: stretch;
    }

    .toggle-btn {
        flex: 1;
    }

    .btn-new-ticket {
        width: 100%;
    }
}

/* Create Ticket Modal */
.create-ticket-modal >>> .modal-content {
    border-radius: 8px;
    border: none;
    box-shadow: 0 20px 60px rgba(9, 30, 66, 0.4);
}

.create-ticket-modal >>> .modal-header {
    border-bottom: 1px solid #dfe1e6;
    padding: 20px 24px;
    background: #fff;
    border-radius: 8px 8px 0 0;
}

.create-ticket-modal >>> .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #172b4d;
    margin: 0;
}

.create-ticket-modal >>> .modal-body {
    padding: 24px;
    background: #fff;
}

.create-ticket-modal >>> .close {
    font-size: 1.5rem;
    font-weight: 300;
    color: #6b778c;
    opacity: 0.7;
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.create-ticket-modal >>> .close:hover {
    opacity: 1;
    color: #172b4d;
}

.create-ticket-modal .form-group {
    margin-bottom: 20px;
}

.create-ticket-modal .form-group label {
    display: block;
    margin-bottom: 6px;
    color: #172b4d;
    font-weight: 600;
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.create-ticket-modal .form-group .required {
    color: #de350b;
    margin-left: 2px;
}

.create-ticket-modal .form-control {
    border: 2px solid #dfe1e6;
    border-radius: 4px;
    padding: 10px 12px;
    font-size: 0.875rem;
    color: #172b4d;
    background-color: #fafbfc;
    transition: all 0.2s ease;
}

.create-ticket-modal .form-control:focus {
    outline: none;
    border-color: #0052cc;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.1);
}

.create-ticket-modal .form-control:read-only {
    background-color: #f4f5f7;
    cursor: not-allowed;
}

.create-ticket-modal .form-control.is-invalid {
    border-color: #de350b;
}

.create-ticket-modal .form-control.is-invalid:focus {
    border-color: #de350b;
    box-shadow: 0 0 0 3px rgba(222, 53, 11, 0.1);
}

.create-ticket-modal .invalid-feedback {
    display: block;
    font-size: 0.75rem;
    color: #de350b;
    margin-top: 4px;
}

.create-ticket-modal .form-row {
    display: flex;
    flex-wrap: wrap;
    margin-left: -10px;
    margin-right: -10px;
}

.create-ticket-modal .form-row .form-group {
    padding-left: 10px;
    padding-right: 10px;
}

.create-ticket-modal .input-group {
    position: relative;
    display: flex !important;
    flex-wrap: nowrap !important;
}

.create-ticket-modal .input-group-append {
    width: auto !important;
    margin-top: 0 !important;
}

.create-ticket-modal .input-group-text {
    background-color: #fafbfc;
    border: 2px solid #dfe1e6;
    border-left: none;
    border-radius: 0 4px 4px 0;
    color: #6b778c;
    padding: 10px 12px;
}

.create-ticket-modal .input-group .form-control {
    border-right: none;
    border-radius: 4px 0 0 4px;
    flex: 1;
    min-width: 0;
}

.create-ticket-modal .input-group .form-control:focus {
    border-right: 2px solid #0052cc;
}

.create-ticket-modal
    .input-group
    .form-control:focus
    + .input-group-append
    .input-group-text {
    border-color: #0052cc;
}

.create-ticket-modal .modal-actions {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
    gap: 24px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #dfe1e6;
    width: 100%;
    flex-wrap: nowrap;
}

.create-ticket-modal .modal-actions .btn-cancel,
.create-ticket-modal .modal-actions .btn-create {
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: 140px;
    text-align: center;
}

.create-ticket-modal .btn-cancel {
    padding: 8px 20px;
    font-weight: 600;
    border-radius: 4px;
    background-color: #f4f5f7;
    border: 2px solid #dfe1e6;
    color: #42526e;
    transition: all 0.2s ease;
}

.create-ticket-modal .btn-cancel:hover {
    background-color: #ebecf0;
    border-color: #b3bac5;
    color: #172b4d;
}

.create-ticket-modal .btn-create {
    padding: 8px 20px;
    font-weight: 600;
    border-radius: 4px;
    background-color: var(--primary);
    border: 1px solid transparent;
    color: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 14px 28px -20px var(--primary-shadow-strong);
}

.create-ticket-modal .btn-create:hover:not(:disabled) {
    background-color: var(--tertiary);
    border-color: transparent;
    box-shadow: 0 18px 34px -22px var(--primary-shadow-strong);
    transform: translateY(-1px);
}

.create-ticket-modal .btn-create:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 14px 28px -20px var(--primary-shadow-strong);
}

.create-ticket-modal .btn-create:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.create-ticket-modal textarea.form-control {
    resize: vertical;
    min-height: 100px;
}

@media (max-width: 768px) {
    .create-ticket-modal >>> .modal-body {
        padding: 20px 16px;
    }

    .create-ticket-modal >>> .modal-dialog {
        margin: 0.5rem;
        max-width: calc(100% - 1rem);
    }

    .create-ticket-modal .form-row {
        flex-direction: column;
        margin-left: 0;
        margin-right: 0;
    }

    .create-ticket-modal .form-row .form-group {
        width: 100%;
        padding-left: 0;
        padding-right: 0;
        margin-bottom: 1rem;
    }

    .create-ticket-modal .form-row .form-group:last-child {
        margin-bottom: 0;
    }

    .create-ticket-modal .input-group {
        width: 100%;
    }

    .create-ticket-modal .input-group .form-control {
        font-size: 16px;
    }

    .create-ticket-modal .modal-actions {
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }

    .create-ticket-modal .btn-cancel,
    .create-ticket-modal .btn-create {
        max-width: 140px;
        flex: 0 0 auto !important;
    }
}
</style>
